/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './StudentDashboard.css';
import Sidebar from './Sidebar';
import Header from './Header';
import HomePage from './HomePage';
import StudentCourses from './StudentCourses';
import StudentExams from './StudentExams';
import StudentResults from './StudentResults';
import ProfilModal from './ProfilModal';
import StudyTopics from './StudyTopics';
import Assignments from './Assignments';
import StudentResources from './StudentResources';
import { apiFetch } from './api';

function StudentDashboard({ user, onLogout, isImpersonating = false, onReturnToAdmin }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  // URL'den sayfa bilgisini al (ör: /student/courses -> 'courses')
  const getPageFromUrl = (pathname) => {
    if (pathname.startsWith('/student/')) {
      const page = pathname.replace('/student/', '');
      if (page === '' || page === 'home') return 'home';
      return page;
    }
    return 'home';
  };

  // localStorage'dan activePage'i yükle, yoksa URL'den al, yoksa 'home' kullan
  const [activePage, setActivePage] = useState(() => {
    const urlPage = getPageFromUrl(location.pathname);
    if (urlPage && urlPage !== 'home') return urlPage;
    const savedPage = localStorage.getItem('studentActivePage');
    return savedPage || 'home';
  });
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isExamMode, setIsExamMode] = useState(false);
  const [examState, setExamState] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth > 768);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const lastPathnameRef = useRef(location.pathname);

  // URL değiştiğinde state'i güncelle (tarayıcı geri/ileri butonu için)
  useEffect(() => {
    // Sadece URL gerçekten değiştiyse (tarayıcı butonu ile) state'i güncelle
    if (location.pathname !== lastPathnameRef.current) {
      lastPathnameRef.current = location.pathname;
      const urlPage = getPageFromUrl(location.pathname);
      if (urlPage !== activePage) {
        setActivePage(urlPage);
        localStorage.setItem('studentActivePage', urlPage);
      }
    }
  }, [location.pathname, activePage]);

  // activePage manuel değiştiğinde (menü tıklaması) URL'i güncelle
  const handlePageChange = (page) => {
    setActivePage(page);
    const pagePath = page === 'home' ? '/student/home' : `/student/${page}`;
    lastPathnameRef.current = pagePath; // Ref'i güncelle ki useEffect tetiklenmesin
    navigate(pagePath); // replace: false - history'ye ekle (geri butonu için)
    localStorage.setItem('studentActivePage', page);
    // Mobilde menü öğesine tıklanınca sidebar'ı kapat
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Profile bilgilerini çek
  useEffect(() => {
    const fetchProfile = async () => {
      if (activePage === 'profile' && user?.id && !profileData) {
        setLoadingProfile(true);
        try {
          const response = await apiFetch('/student/profile');
          const data = await response.json();
          if (data.success && data.profile) {
            setProfileData(data.profile);
          }
        } catch (err) {
          console.error('Profile fetch error:', err);
        } finally {
          setLoadingProfile(false);
        }
      }
    };
    fetchProfile();
  }, [activePage, user?.id, profileData]);

  // Mobil kontrolü
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Desktop'ta sidebar her zaman açık, mobilde kapalı
      if (!mobile) {
        setIsSidebarOpen(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const renderPageContent = () => {
    switch (activePage) {
      case 'home':
        return <HomePage userType="student" onPageChange={handlePageChange} />;
      
      case 'courses':
        return <StudentCourses studentId={user?.id} />;
      
      case 'exams':
        return <StudentExams
          key={`exams-${user?.id}`}
          studentId={user?.id}
          user={user}
          onExamStatusChange={(value) => {
            setIsExamMode(value);
          }}
          onExamStateChange={(hasExam) => {
            setExamState(hasExam);
            setIsExamMode(hasExam);
            // Sınav modunda activePage'i exams'te tut
            if (hasExam && activePage !== 'exams') {
              handlePageChange('exams');
            }
          }}
        />;
      
      case 'results':
        return <StudentResults studentId={user?.id} />;

      case 'calisma-konulari':
        return <StudyTopics userType="student" />;

      case 'odevler':
        return <Assignments userType="student" />;

      case 'kaynak-onerileri':
        return <StudentResources />;

      case 'profile':
        return (
          <div className="dashboard-section">
            <h2>Bilgilerim</h2>
            {loadingProfile ? (
              <div className="loading-state">
                <p>Yükleniyor...</p>
              </div>
            ) : (
              <div className="info-card">
                <div className="info-item">
                  <span className="info-label">Öğrenci Numarası:</span>
                  <span className="info-value">{user?.student_number || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Öğrenci ID:</span>
                  <span className="info-value">{user?.id || '-'}</span>
                </div>
                {user?.name && (
                  <div className="info-item">
                    <span className="info-label">Ad Soyad:</span>
                    <span className="info-value">{user.name}</span>
                  </div>
                )}
                {profileData?.profil_bilgileri?.email && (
                  <div className="info-item">
                    <span className="info-label">E-posta:</span>
                    <span className="info-value">{profileData.profil_bilgileri.email}</span>
                  </div>
                )}
                {profileData?.akademik_bilgiler?.fakulte && (
                  <div className="info-item">
                    <span className="info-label">Fakülte:</span>
                    <span className="info-value">
                      {profileData.akademik_bilgiler.fakulte.replace(/^Fakülte\s*:\s*/i, '').trim() || '-'}
                    </span>
                  </div>
                )}
                {profileData?.akademik_bilgiler?.program && (
                  <div className="info-item">
                    <span className="info-label">Bölüm:</span>
                    <span className="info-value">
                      {profileData.akademik_bilgiler.program.replace(/^Program:\s*/i, '').trim() || '-'}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  // Sınav modunda sidebar ve header'ı gizle - StudentExams içinde kontrol ediliyor
  // isExamMode kontrolü kaldırıldı çünkü StudentExams component'i yeniden mount ediliyordu

  return (
    <div className={`student-dashboard ${isExamMode ? 'exam-mode' : ''}`}>
      {!isExamMode && (
        <>
          <Header
            user={user}
            onLogout={onLogout}
            onProfileClick={() => setIsProfileModalOpen(true)}
            onHomeClick={() => handlePageChange('home')}
            onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
            title="KOSTÜ Sınav Modülü"
          />
          
          <Sidebar
            userType="student"
            activePage={activePage}
            onPageChange={handlePageChange}
            user={user}
            isImpersonating={isImpersonating}
            onReturnToAdmin={onReturnToAdmin}
            isOpen={isMobile ? isSidebarOpen : true} // Desktop'ta her zaman açık
            onClose={isMobile ? () => setIsSidebarOpen(false) : undefined} // Desktop'ta kapatma butonu yok
          />
        </>
      )}
      
      <div className={`dashboard-main ${isExamMode ? 'exam-mode' : ''}`}>
        <div className={`dashboard-content ${activePage === 'home' ? 'full-height' : ''}`}>
          {activePage === 'home' ? (
            renderPageContent()
          ) : (
            <div className="dashboard-container">
              {renderPageContent()}
            </div>
          )}
        </div>
      </div>

      <ProfilModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        user={user}
        userType="student"
      />
    </div>
  );
}

export default StudentDashboard;
